import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from '@structura/i18n'
import './About.css'

export interface AboutPageProps {
  // The current app's version string, displayed on the About page. Passed in
  // by each app since the version belongs to the app's package.json, not the
  // shared @structura/pages package.
  appVersion: string

  // Per-app beam designer route (e.g. "/beam/wood"). The CTA "Try Beam
  // Designer" links here. Without parameterization a wood/steel user clicking
  // the CTA would land on the concrete app's designer route.
  beamRoute: string
}

const About: React.FC<AboutPageProps> = ({ appVersion, beamRoute }) => {
  const { t } = useTranslation()

  return (
    <div className="about-container">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <img 
                src="/logo.svg" 
                alt={t('pages.logoAlt')}
                className="about-logo mb-4"
              />
              <h1 className="about-title">
                Structura<span className="highlight">Matrix</span>
              </h1>
              <p className="about-subtitle">
                {t('pages.about.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="about-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">
              <div className="mission-card">
                <div className="mission-visual">
                  <div className="mission-shell"></div>
                  <div className="mission-grid"></div>
                </div>
                <div className="mission-copy">
                  <h2 className="section-title">{t('pages.about.ourMission')}</h2>
                  <p className="mission-text">
                    {t('pages.about.missionText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="about-section features-section">
        <div className="container">
          <h2 className="section-title text-center mb-5">{t('pages.about.whatWeOffer')}</h2>
          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="feature-box">
                <div className="feature-icon">
                  <i className="bi bi-hammer"></i>
                </div>
                <h3>{t('pages.about.beamDesigner')}</h3>
                <p>{t('pages.about.beamDesignerText')}</p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="feature-box">
                <div className="feature-icon">
                  <i className="bi bi-stars"></i>
                </div>
                <h3>Structura-Bits</h3>
                <p>{t('pages.about.structuraBitsText')}</p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="feature-box">
                <div className="feature-icon">
                  <i className="bi bi-check-circle"></i>
                </div>
                <h3>{t('pages.about.eurocodeCompliance')}</h3>
                <p>{t('pages.about.eurocodeText')}</p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="feature-box">
                <div className="feature-icon">
                  <i className="bi bi-graph-up"></i>
                </div>
                <h3>{t('pages.about.visualResults')}</h3>
                <p>{t('pages.about.visualResultsText')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="about-section team-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">
              <div className="team-card">
                <div className="team-visual">
                  <div className="team-shell"></div>
                  <div className="team-grid"></div>
                  <div className="team-orbit"></div>
                </div>
                <div className="team-copy">
                  <h2 className="section-title team-title">{t('pages.about.theTeam')}</h2>
                  <p className="team-intro">
                    {t('pages.about.teamIntro')}
                  </p>
                  <div className="team-details">
                    <p>
                      {t('pages.about.teamDetail1')}
                    </p>
                    <p>
                      {t('pages.about.teamDetail2')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Design Philosophy */}
      <section className="about-section logo-section">
        <div className="container">
          <div className="row justify-content-center align-items-center">
            <div className="col-lg-4 text-center">
              <img 
                src="/logo.svg" 
                alt={t('pages.logoAlt')}
                className="logo-large"
              />
            </div>
            <div className="col-lg-6">
              <h2 className="section-title">{t('pages.about.logoStory')}</h2>
              <p className="logo-description">
                {t('pages.about.logoDesc1')}
              </p>
              <p className="logo-description">
                {t('pages.about.logoDesc2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Version Info */}
      <section className="about-section version-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="version-card">
                <h2 className="section-title text-center">{t('pages.about.versionInfo')}</h2>
                <div className="version-info">
                  <div className="version-row">
                    <span className="version-label">{t('pages.about.currentVersion')}</span>
                    <span className="version-value">{appVersion}</span>
                  </div>
                  <div className="version-row">
                    <span className="version-label">{t('pages.about.releaseDate')}</span>
                    <span className="version-value">{t('pages.about.releaseDateValue')}</span>
                  </div>
                  <div className="version-row">
                    <span className="version-label">{t('pages.about.platform')}</span>
                    <span className="version-value">{t('pages.about.platformValue')}</span>
                  </div>
                  <div className="version-row">
                    <span className="version-label">{t('pages.about.status')}</span>
                    <span className="version-value">
                      <span className="status-badge">{t('pages.about.statusValue')}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="about-section cta-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <h2 className="section-title">{t('pages.about.startToday')}</h2>
              <p className="cta-text">
                {t('pages.about.ctaText')}
              </p>
              <div className="cta-buttons">
                <Link to={beamRoute} className="btn btn-primary btn-lg">
                  <i className="bi bi-hammer me-2"></i>
                  {t('pages.about.tryBeamDesigner')}
                </Link>
                <Link to="/structura-bits" className="btn btn-outline-primary btn-lg">
                  <i className="bi bi-stars me-2"></i>
                  {t('pages.about.exploreStructuraBits')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About

